import React, { useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Leaf, Shield, Award, Sparkles, Package, ChefHat, 
  Building2, Truck, CheckCircle2, ArrowRight, Clock,
  Phone, Mail, ArrowLeft, Flame, MessageCircle, Bell, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

// Product data - easily extendable for new products
const productsData = {
  'chilli-powder': {
    id: 'chilli-powder',
    name: 'Premium Chilli Powder',
    malayalamName: 'മുളകുപൊടി',
    tagline: 'The Heart of South Indian Cuisine',
    shortDescription: 'Experience the authentic taste of carefully selected red chillies, expertly ground to deliver rich color and balanced heat in every dish.',
    overview: 'Our Premium Chilli Powder is crafted from handpicked, sun-dried red chillies sourced from the finest farms across India. The result is a vibrant, aromatic spice that brings authentic flavor to your cooking. With its rich natural color and perfectly balanced heat profile, this chilli powder is the trusted choice for professional kitchens and home cooks who demand nothing but the best.',
    flavorProfile: 'Rich, warm heat with subtle earthy undertones',
    aroma: 'Deep, smoky fragrance with natural pungency',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp',
    highlights: [
      { icon: 'leaf', text: 'Natural rich red color - no artificial additives' },
      { icon: 'award', text: 'Balanced heat profile for versatile cooking' },
      { icon: 'sparkles', text: 'Fine grind consistency for better dispersion' },
      { icon: 'shield', text: 'Hygienically processed in modern facilities' },
      { icon: 'package', text: 'Secure packaging to preserve freshness' }
    ],
    usage: ['Curries & Gravies', 'Marinades & Rubs', 'Fry Preparations', 'Traditional South Indian Dishes', 'Chutneys & Pickles'],
    available: true,
    category: 'Powder Spices'
  },
  'coriander-powder': {
    id: 'coriander-powder',
    name: 'Coriander Powder',
    malayalamName: 'മല്ലിപ്പൊടി',
    tagline: 'The Foundation of Indian Flavors',
    shortDescription: 'Freshly ground coriander seeds delivering an earthy, citrusy aroma that forms the aromatic base of countless Indian recipes.',
    overview: 'Our Coriander Powder is made from premium coriander seeds, carefully roasted and ground to preserve their essential oils and distinctive flavor. This versatile spice is fundamental to Indian cooking, providing the aromatic foundation for curries, dals, and countless other dishes. Its subtle citrus notes and warm undertones make it indispensable in both professional and home kitchens.',
    flavorProfile: 'Earthy, citrusy with mild sweetness',
    aroma: 'Fresh, aromatic with subtle floral notes',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Coriander%20Powder.webp',
    highlights: [
      { icon: 'leaf', text: 'Made from premium whole coriander seeds' },
      { icon: 'award', text: 'Light roasting preserves essential oils' },
      { icon: 'sparkles', text: 'Fresh, aromatic profile in every batch' },
      { icon: 'shield', text: 'No preservatives or artificial flavors' },
      { icon: 'package', text: 'Airtight packaging for lasting freshness' }
    ],
    usage: ['Curry Bases', 'Dal & Lentil Dishes', 'Vegetable Preparations', 'Meat Marinades', 'Spice Blends'],
    available: true,
    category: 'Powder Spices'
  },
  'turmeric-powder': {
    id: 'turmeric-powder',
    name: 'Turmeric Powder',
    malayalamName: 'മഞ്ഞൾപ്പൊടി',
    tagline: 'Golden Purity from Nature',
    shortDescription: 'Premium turmeric with high curcumin content, bringing brilliant golden color and subtle earthy warmth to your culinary creations.',
    overview: 'Our Turmeric Powder is sourced from select farms known for producing turmeric with exceptionally high curcumin content. This golden spice has been a cornerstone of Indian cooking and traditional wellness for centuries. With its vibrant color, subtle earthy flavor, and natural properties, our turmeric powder elevates both the appearance and nutritional value of your dishes.',
    flavorProfile: 'Earthy, slightly bitter with warm undertones',
    aroma: 'Warm, musky with subtle ginger notes',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Turmeric%20Powder.webp',
    highlights: [
      { icon: 'leaf', text: 'High curcumin content for maximum benefits' },
      { icon: 'award', text: 'Brilliant golden color - 100% natural' },
      { icon: 'sparkles', text: 'Ultra-fine grind for smooth blending' },
      { icon: 'shield', text: 'Lab tested for purity and potency' },
      { icon: 'package', text: 'Light-protected packaging preserves quality' }
    ],
    usage: ['Curry Preparations', 'Rice Dishes', 'Traditional Remedies', 'Beverages & Golden Milk', 'Pickles & Preserves'],
    available: true,
    category: 'Powder Spices'
  },
  'kashmiri-chilli-powder': {
    id: 'kashmiri-chilli-powder',
    name: 'Kashmiri Chilli Powder',
    malayalamName: 'കാശ്മീരി മുളകുപൊടി',
    tagline: 'Brilliant Color, Mild Heat',
    shortDescription: 'Prized for its brilliant red color with mild heat. Perfect when you want vibrant color without overpowering spice.',
    overview: 'Our Kashmiri Chilli Powder is sourced from premium Kashmiri chillies, celebrated worldwide for their stunning bright red color and mild heat profile. This spice is a favorite among professional chefs and home cooks who want to add restaurant-quality visual appeal to their dishes without overwhelming heat. Ideal for biryanis, tandoori preparations, and any dish where color matters as much as flavor.',
    flavorProfile: 'Mild heat with slightly sweet, fruity notes',
    aroma: 'Mild, pleasant with subtle sweetness',
    image: 'https://storage.googleapis.com/agniya-images/website/images/Kashmirii%20Chilli%20Powder.webp',
    highlights: [
      { icon: 'leaf', text: 'Bright vibrant red color - naturally stunning' },
      { icon: 'award', text: 'Mild heat level - perfect for color' },
      { icon: 'sparkles', text: 'Premium Kashmiri variety' },
      { icon: 'shield', text: 'No artificial colors or additives' },
      { icon: 'package', text: 'Light-protected packaging preserves color' }
    ],
    usage: ['Biryanis & Pulaos', 'Tandoori Preparations', 'Restaurant-Style Curries', 'Gravies Where Color Matters', 'Marinades'],
    available: true,
    category: 'Powder Spices'
  },
  'garam-masala': {
    id: 'garam-masala',
    name: 'Garam Masala',
    malayalamName: 'ഗരം മസാല',
    tagline: 'The Art of Aromatic Blending',
    shortDescription: 'A masterfully crafted blend of premium whole spices, delivering warmth and complexity that elevates any dish to perfection.',
    overview: 'Our Garam Masala represents the pinnacle of spice blending artistry. This aromatic mix combines cardamom, cinnamon, cloves, cumin, and other premium whole spices, each carefully selected and proportioned. The result is a warming, complex blend that adds depth and sophistication to biryanis, curries, and grilled preparations.',
    flavorProfile: 'Warm, aromatic with complex layered notes',
    aroma: 'Rich, fragrant blend of sweet and warm spices',
    image: 'https://storage.googleapis.com/agniya-images/Garam_Masala.png',
    highlights: [
      { icon: 'leaf', text: 'Blend of 12+ premium whole spices' },
      { icon: 'award', text: 'Traditional recipe with modern precision' },
      { icon: 'sparkles', text: 'Small-batch production for freshness' },
      { icon: 'shield', text: 'No fillers or artificial enhancers' },
      { icon: 'package', text: 'Vacuum-sealed for aroma preservation' }
    ],
    usage: ['Biryanis & Pulaos', 'Rich Curries', 'Grilled Meats', 'Finishing Spice', 'Special Occasion Dishes'],
    available: false,
    comingSoon: true,
    category: 'Spice Blends',
    comingSoonMessage: 'Our master blenders are perfecting the proportions of 12+ aromatic spices to create a garam masala that will transform your cooking.',
    expectedLaunch: 'Coming in early 2026'
  },
  'dried-spices': {
    id: 'dried-spices',
    name: 'Dried Whole Spices',
    malayalamName: 'ഉണങ്ങിയ സുഗന്ധവ്യഞ്ജനങ്ങൾ',
    tagline: 'Whole Spices, Complete Flavor',
    shortDescription: 'A curated selection of premium whole dried spices for authentic tempering and slow-cooked preparations that demand true depth of flavor.',
    overview: 'Our Dried Whole Spices collection features hand-selected cardamom, cloves, cinnamon, bay leaves, and other essential whole spices. These unground spices retain their essential oils and deliver maximum flavor when toasted or tempered. Perfect for professional kitchens that demand authentic results and home cooks seeking restaurant-quality dishes.',
    flavorProfile: 'Complex, multi-layered with intense aromatics',
    aroma: 'Intense, long-lasting fragrance',
    image: 'https://storage.googleapis.com/agniya-images/Whole_Dried_Spices.png',
    highlights: [
      { icon: 'leaf', text: 'Sourced from premium growing regions' },
      { icon: 'award', text: 'Whole spices retain maximum flavor' },
      { icon: 'sparkles', text: 'Perfect for tempering and slow cooking' },
      { icon: 'shield', text: 'Grade-A quality in every pack' },
      { icon: 'package', text: 'Resealable packaging for convenience' }
    ],
    usage: ['Tempering (Tadka)', 'Slow-Cooked Dishes', 'Rice & Biryanis', 'Spice Infusions', 'Pickling'],
    available: false,
    comingSoon: true,
    category: 'Whole Spices',
    comingSoonMessage: 'We are carefully curating the finest whole spices from across India - cardamom from Kerala, cinnamon from Sri Lanka, and more.',
    expectedLaunch: 'Coming in early 2026'
  }
};

// Packaging options
const packagingOptions = {
  retail: [
    { size: '50g', description: 'Trial Pack', ideal: 'First-time users' },
    { size: '100g', description: 'Home Pack', ideal: 'Small households' },
    { size: '250g', description: 'Family Pack', ideal: 'Regular cooking' },
    { size: '500g', description: 'Value Pack', ideal: 'Frequent users' }
  ],
  bulk: [
    { size: '1 kg', description: 'Kitchen Pack', ideal: 'Large families' },
    { size: '5 kg', description: 'Commercial Pack', ideal: 'Small restaurants' },
    { size: '10 kg', description: 'Bulk Pack', ideal: 'Caterers & Hotels' }
  ]
};

// Icon mapping
const iconMap = {
  leaf: Leaf,
  shield: Shield,
  award: Award,
  sparkles: Sparkles,
  package: Package
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const product = productsData[productId];

  // Scroll to top when product changes (fixes related product navigation not scrolling)
  useEffect(() => {
    // Smooth scroll to top for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  // Handle invalid product
  if (!product) {
    return (
      <div className="min-h-screen bg-[#f7f5f2] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#8B2820] mb-4">Product Not Found</h1>
          <p className="text-[#4a3a33] mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/#products">
            <Button className="bg-[#8B2820] hover:bg-[#6d1f19]">
              View All Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get related products (exclude current)
  const relatedProducts = Object.values(productsData)
    .filter(p => p.id !== product.id)
    .slice(0, 3);

  // Navigate to contact form on homepage
  const navigateToContact = () => {
    // Navigate to homepage with state indicating to scroll to contact
    navigate('/', { state: { scrollTo: 'contact-form' } });
  };
  
  // Generate meta title based on product
  const getMetaTitle = () => {
    switch (product.id) {
      case 'chilli-powder':
        return 'Red Chilli Powder | Best Chilli Powder for Cooking | Agniya Spices';
      case 'kashmiri-chilli-powder':
        return 'Kashmiri Chilli Powder | Best for Biriyani & Color | Agniya Spices';
      case 'turmeric-powder':
        return 'Turmeric Powder | Pure Haldi Powder for Cooking | Agniya Spices';
      case 'coriander-powder':
        return 'Coriander Powder | Fresh Dhaniya Powder | Agniya Spices';
      default:
        return `${product.name} - Agniya Spices | Premium Quality Indian Spices`;
    }
  };
  
  // Generate meta description based on product
  const getMetaDescription = () => {
    switch (product.id) {
      case 'chilli-powder':
        return 'Buy red chilli powder for Indian cooking. Perfect for curries, fish fry, and masala dishes.';
      case 'kashmiri-chilli-powder':
        return 'Kashmiri chilli powder gives rich red color with mild heat. Ideal for biriyani and gravies.';
      case 'turmeric-powder':
        return 'Shop turmeric powder for daily cooking. Learn how to choose good turmeric and check purity.';
      case 'coriander-powder':
        return 'Coriander powder adds aroma and balance to Indian cooking. Ideal for curries and masala.';
      default:
        return product.shortDescription;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{getMetaTitle()}</title>
        <meta 
          name="description" 
          content={getMetaDescription()}
        />
        <meta 
          name="keywords" 
          content={`${product.name}, ${product.malayalamName}, Agniya Spices, Indian spices, Kerala spices, bulk spices, restaurant spices`}
        />
        <link rel="canonical" href={`https://agniyaspices.com/products/${product.id}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={getMetaTitle()} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://agniyaspices.com/products/${product.id}`} />
        <meta property="og:site_name" content="Agniya Spices" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getMetaTitle()} />
        <meta name="twitter:description" content={getMetaDescription()} />
        <meta name="twitter:image" content={product.image} />
        
        {/* Product Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "image": product.image,
            "description": product.overview,
            "brand": {
              "@type": "Brand",
              "name": "Agniya Spices"
            },
            "offers": {
              "@type": "AggregateOffer",
              "availability": product.available ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
              "priceCurrency": "INR",
              "seller": {
                "@type": "Organization",
                "name": "Agniya Spices"
              }
            }
          })}
        </script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://agniyaspices.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Products",
                "item": "https://agniyaspices.com/#products"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": product.name,
                "item": `https://agniyaspices.com/products/${product.id}`
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">
              AGNIYA SPICES
            </div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#8B2820] font-semibold transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">FAQ</Link>
            <Link to="/#quality" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Quality</Link>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="pt-20 bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center text-sm text-[#736c64]">
            <Link to="/" className="hover:text-[#8B2820] transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/#products" className="hover:text-[#8B2820] transition-colors">Products</Link>
            <span className="mx-2">/</span>
            <span className="text-[#8B2820] font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Section 1: Hero Product Section */}
      <section className="py-12 md:py-20 bg-white" data-testid="product-hero">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Product Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {product.comingSoon && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end justify-center pb-12">
                    <Badge className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 text-lg">
                      <Clock className="w-5 h-5 mr-2" />
                      Coming Soon
                    </Badge>
                  </div>
                )}
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#8B2820]/10 rounded-full -z-10" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#8B2820]/5 rounded-full -z-10" />
            </div>

            {/* Product Info */}
            <div className="lg:pl-8">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="outline" className="text-[#8B2820] border-[#8B2820]/30">
                  {product.category}
                </Badge>
                {product.comingSoon && (
                  <Badge className="bg-amber-500 text-white">
                    <Clock className="w-3 h-3 mr-1" />
                    Launching Soon
                  </Badge>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#8B2820] mb-2">
                {product.name}
              </h1>
              <p className="text-xl md:text-2xl text-[#8B2820]/70 mb-4 font-medium">
                {product.malayalamName}
              </p>
              
              <p className="text-2xl md:text-3xl text-[#4a3a33] font-light mb-6 italic">
                "{product.tagline}"
              </p>

              <p className="text-lg text-[#4a3a33] leading-relaxed mb-8">
                {product.shortDescription}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                {product.available ? (
                  <>
                    <Button 
                      size="lg"
                      className="bg-[#8B2820] hover:bg-[#6d1f19] text-white px-8 py-6 text-lg shadow-lg transition-all duration-300 hover:scale-105"
                      data-testid="request-quote-btn"
                      onClick={navigateToContact}
                    >
                      <Building2 className="mr-2 h-5 w-5" />
                      Request Bulk Quote
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820] hover:text-white px-8 py-6 text-lg transition-all duration-300"
                      data-testid="contact-sales-btn"
                      onClick={navigateToContact}
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Contact Sales
                    </Button>
                  </>
                ) : (
                  <Button 
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg shadow-lg"
                    data-testid="notify-btn"
                    onClick={navigateToContact}
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    Notify Me When Available
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Product Overview */}
      <section className="py-16 md:py-20 bg-[#f7f5f2]" data-testid="product-overview">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-8 text-center">
              Product Overview
            </h2>
            
            <p className="text-lg md:text-xl text-[#4a3a33] leading-relaxed mb-10 text-center">
              {product.overview}
            </p>

            {/* Flavor & Aroma Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-[#8B2820]/10 rounded-full mr-4">
                      <ChefHat className="h-6 w-6 text-[#8B2820]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#4a3a33]">Flavor Profile</h3>
                  </div>
                  <p className="text-[#736c64]">{product.flavorProfile}</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-[#8B2820]/10 rounded-full mr-4">
                      <Sparkles className="h-6 w-6 text-[#8B2820]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#4a3a33]">Aroma</h3>
                  </div>
                  <p className="text-[#736c64]">{product.aroma}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Key Highlights */}
      <section className="py-16 md:py-20 bg-white" data-testid="product-highlights">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-12 text-center">
            Key Highlights
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {product.highlights.map((highlight, index) => {
                const IconComponent = iconMap[highlight.icon] || CheckCircle2;
                return (
                  <div 
                    key={index}
                    className="flex items-start p-6 bg-[#f7f5f2] rounded-xl hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="p-3 bg-[#8B2820]/10 rounded-full mr-4 flex-shrink-0">
                      <IconComponent className="h-6 w-6 text-[#8B2820]" />
                    </div>
                    <p className="text-[#4a3a33] text-lg leading-relaxed">
                      {highlight.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Packaging Options - Only for available products */}
      {!product.comingSoon && (
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#f7f5f2] to-[#ebe7e2]" data-testid="packaging-options">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-4 text-center">
              Packaging Options
            </h2>
            <p className="text-lg text-[#4a3a33] text-center mb-12 max-w-2xl mx-auto">
              Available in convenient retail sizes and cost-effective bulk packaging for businesses
            </p>

            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Retail Packs */}
              <Card className="border-none shadow-xl bg-white overflow-hidden">
                <div className="bg-[#8B2820] text-white p-6">
                  <div className="flex items-center">
                    <Package className="h-8 w-8 mr-3" />
                    <div>
                      <h3 className="text-2xl font-bold">Retail Packs</h3>
                      <p className="text-white/80 text-sm">For households & small kitchens</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {packagingOptions.retail.map((pack, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-4 bg-[#f7f5f2] rounded-lg hover:bg-[#ebe7e2] transition-colors"
                      >
                        <div className="flex items-center">
                          <span className="text-2xl font-bold text-[#8B2820] w-16">{pack.size}</span>
                          <div className="ml-4">
                            <p className="font-medium text-[#4a3a33]">{pack.description}</p>
                            <p className="text-sm text-[#736c64]">Ideal for {pack.ideal}</p>
                          </div>
                        </div>
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bulk Packs */}
              <Card className="border-none shadow-xl bg-white overflow-hidden">
                <div className="bg-[#4a3a33] text-white p-6">
                  <div className="flex items-center">
                    <Truck className="h-8 w-8 mr-3" />
                    <div>
                      <h3 className="text-2xl font-bold">Bulk Packs</h3>
                      <p className="text-white/80 text-sm">For restaurants & distributors</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {packagingOptions.bulk.map((pack, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-4 bg-[#f7f5f2] rounded-lg hover:bg-[#ebe7e2] transition-colors"
                      >
                        <div className="flex items-center">
                          <span className="text-2xl font-bold text-[#4a3a33] w-16">{pack.size}</span>
                          <div className="ml-4">
                            <p className="font-medium text-[#4a3a33]">{pack.description}</p>
                            <p className="text-sm text-[#736c64]">Ideal for {pack.ideal}</p>
                          </div>
                        </div>
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Custom Packaging Note */}
            <div className="max-w-3xl mx-auto mt-8">
              <Card className="border-2 border-dashed border-[#8B2820]/30 bg-white/50">
                <CardContent className="p-6 text-center">
                  <Building2 className="h-10 w-10 text-[#8B2820] mx-auto mb-3" />
                  <p className="text-lg text-[#4a3a33] font-medium">
                    Custom packaging available for distributors and bulk buyers
                  </p>
                  <p className="text-[#736c64] mt-2">
                    Contact us to discuss your specific requirements
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Section 4 Alternative: Blending in Progress - For Coming Soon products */}
      {product.comingSoon && (
        <section className="py-16 md:py-20 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="blending-in-progress">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              {/* Animated Icon */}
              <div className="relative inline-flex mb-8">
                <div className="p-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full shadow-xl">
                  <Flame className="h-12 w-12 text-white animate-pulse" />
                </div>
                <div className="absolute -top-2 -right-2 p-2 bg-white rounded-full shadow-lg">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-amber-700 mb-6">
                Blending in Progress
              </h2>
              
              <p className="text-xl md:text-2xl text-amber-800/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                {product.comingSoonMessage}
              </p>

              {/* Progress Indicators */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                {[
                  { title: 'Sourcing', desc: 'Premium ingredients selected', done: true },
                  { title: 'Perfecting', desc: 'Recipe refinement in progress', done: false, current: true },
                  { title: 'Packaging', desc: 'Design being finalized', done: false }
                ].map((step, index) => (
                  <Card key={index} className={`border-none shadow-lg ${step.current ? 'ring-2 ring-amber-400' : ''}`}>
                    <CardContent className="p-6 text-center">
                      <div className={`inline-flex p-3 rounded-full mb-3 ${
                        step.done ? 'bg-green-100' : step.current ? 'bg-amber-100' : 'bg-gray-100'
                      }`}>
                        {step.done ? (
                          <CheckCircle2 className="h-6 w-6 text-green-600" />
                        ) : step.current ? (
                          <Sparkles className="h-6 w-6 text-amber-600 animate-pulse" />
                        ) : (
                          <Clock className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      <h4 className={`font-bold ${step.current ? 'text-amber-700' : 'text-[#4a3a33]'}`}>
                        {step.title}
                      </h4>
                      <p className="text-sm text-[#736c64] mt-1">{step.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Expected Launch */}
              <Badge className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 text-lg">
                {product.expectedLaunch}
              </Badge>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Ideal Usage */}
      <section className="py-16 md:py-20 bg-white" data-testid="ideal-usage">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-4 text-center">
            Ideal Usage
          </h2>
          <p className="text-lg text-[#4a3a33] text-center mb-12 max-w-2xl mx-auto">
            Perfect for a wide range of culinary applications
          </p>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-4">
              {product.usage.map((use, index) => (
                <div 
                  key={index}
                  className="flex items-center bg-gradient-to-r from-[#8B2820]/5 to-[#8B2820]/10 px-6 py-4 rounded-full hover:shadow-md transition-shadow"
                >
                  <ChefHat className="h-5 w-5 text-[#8B2820] mr-3" />
                  <span className="text-[#4a3a33] font-medium">{use}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Quality Commitment */}
      <section className="py-16 md:py-20 bg-[#f7f5f2]" data-testid="quality-commitment">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-4 text-center">
            Our Quality Commitment
          </h2>
          <p className="text-lg text-[#4a3a33] text-center mb-12 max-w-2xl mx-auto">
            Every batch of Agniya Spices meets our rigorous quality standards
          </p>

          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Leaf, title: 'Sourced', desc: 'Carefully selected raw materials from trusted farms' },
              { icon: Shield, title: 'Processed', desc: 'Hygienic processing in modern facilities' },
              { icon: Award, title: 'Tested', desc: 'Consistent quality in every batch' },
              { icon: Package, title: 'Packed', desc: 'Secure packaging for lasting freshness' }
            ].map((item, index) => (
              <Card key={index} className="border-none shadow-lg bg-white text-center group hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="p-4 bg-[#8B2820]/10 rounded-full inline-flex mb-4 group-hover:bg-[#8B2820] group-hover:text-white transition-colors">
                    <item.icon className="h-8 w-8 text-[#8B2820] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[#4a3a33] mb-2">{item.title}</h3>
                  <p className="text-[#736c64] text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Bulk Buyer CTA - Different for Available vs Coming Soon */}
      {!product.comingSoon ? (
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#8B2820] to-[#5d1a15] text-white" data-testid="bulk-cta">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Building2 className="h-16 w-16 mx-auto mb-6 opacity-80" />
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Supplying to Restaurants & Distributors
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                Partner with Agniya Spices for consistent quality, competitive pricing, and reliable supply for your business
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-white text-[#8B2820] hover:bg-white/90 px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105"
                  data-testid="bulk-pricing-btn"
                  onClick={navigateToContact}
                >
                  Request Bulk Pricing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#8B2820] px-10 py-6 text-lg transition-all duration-300"
                  data-testid="distributor-btn"
                  onClick={navigateToContact}
                >
                  Distributor Enquiry
                </Button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24 bg-gradient-to-br from-amber-500 to-orange-600 text-white" data-testid="coming-soon-cta">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="relative inline-flex mb-6">
                <MessageCircle className="h-16 w-16 opacity-90" />
                <Bell className="h-8 w-8 absolute -top-2 -right-2 animate-bounce" />
              </div>
              
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Curious About This Product?
              </h2>
              <p className="text-xl text-white/90 mb-4 max-w-2xl mx-auto">
                We're crafting something special and would love to hear from you.
              </p>
              <p className="text-lg text-white/80 mb-10 max-w-xl mx-auto">
                Drop us a message to learn more about our upcoming {product.name}, get early access updates, or discuss bulk requirements for your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-white text-amber-600 hover:bg-white/90 px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105"
                  data-testid="send-message-btn"
                  onClick={navigateToContact}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Send Us a Message
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-amber-600 px-10 py-6 text-lg transition-all duration-300"
                  data-testid="notify-launch-btn"
                  onClick={navigateToContact}
                >
                  <Bell className="mr-2 h-5 w-5" />
                  Notify Me on Launch
                </Button>
              </div>

              {/* Trust Note */}
              <p className="mt-8 text-white/70 text-sm">
                We typically respond within 24 hours. Your inquiry helps us understand what matters most to you.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Section 8: Related Products */}
      <section className="py-16 md:py-20 bg-white" data-testid="related-products">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2820] mb-4 text-center">
            Related Products
          </h2>
          <p className="text-lg text-[#4a3a33] text-center mb-12 max-w-2xl mx-auto">
            Explore our other premium spice offerings
          </p>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedProducts.map((relatedProduct) => (
              <Link 
                key={relatedProduct.id}
                to={`/products/${relatedProduct.id}`}
                className="group"
              >
                <Card className="border-none shadow-lg overflow-hidden h-full hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {relatedProduct.comingSoon && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge className="bg-amber-500 text-white">Coming Soon</Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <Badge variant="outline" className="text-xs mb-2 text-[#736c64]">
                      {relatedProduct.category}
                    </Badge>
                    <h3 className="text-xl font-bold text-[#4a3a33] mb-1 group-hover:text-[#8B2820] transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-sm text-[#8B2820]/70 mb-3">{relatedProduct.malayalamName}</p>
                    <p className="text-[#736c64] text-sm line-clamp-2">{relatedProduct.shortDescription}</p>
                    <div className="mt-4 flex items-center text-[#8B2820] font-medium text-sm group-hover:underline">
                      View Details
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Back to Products Link */}
      <section className="py-8 bg-[#f7f5f2] border-t">
        <div className="container mx-auto px-4 text-center">
          <Link to="/#products">
            <Button variant="ghost" className="text-[#8B2820] hover:bg-[#8B2820]/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to All Products
            </Button>
          </Link>
        </div>
      </section>

      <Footer productsData={productsData} copyrightYear={2026} />
    </div>
  );
};

export default ProductDetailPage;
